import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef, useCallback } from 'react';
import { z } from 'zod';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import {
  FiArrowLeft,
  FiArrowRight,
  FiMapPin,
  FiUser,
  FiUsers,
  FiRefreshCw,
  FiSearch,
} from 'react-icons/fi';
import { apiClient } from '../api/api';
import { getProfileIdentifier } from '../utils/profileUrl';
import { getDiscoverAvatarUrl } from '../utils/avatar';
import { LoadingLogo } from '../components/common/LoadingLogo';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FilteredUser {
  id: number;
  username?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  prof_pics_url?: string | null;
  google_prof_pics?: string | null;
  city?: string;
  state?: string;
  country?: string;
  user_types?: { id: number; name: string }[];
  genres?: string[];
  follower_count?: number;
}

interface FilteredUsersResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: FilteredUser[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const USER_TYPE_LABELS: Record<string, string> = {
  artist: 'Artist',
  dj: 'DJ',
  event_producer: 'Event Producer',
  venue: 'Venue',
  crew: 'Crew',
  fan: 'Fan',
};

function getPageTitle(userType: string, city?: string): string {
  const label = USER_TYPE_LABELS[userType] ?? userType;
  // Simple pluralise
  const plural = label.toLowerCase().endsWith('y') ? `${label.slice(0, -1)}ies` : `${label}s`;
  if (city && city !== 'All Locations') {
    return `${plural} in ${city}`;
  }
  return `All ${plural}`;
}

function getPageSubtitle(userType: string, city?: string): string {
  if (city && city !== 'All Locations') {
    return `Browse ${USER_TYPE_LABELS[userType] ?? userType}s based in ${city}`;
  }
  return `Browse all ${USER_TYPE_LABELS[userType] ?? userType}s on Musicade`;
}

const PAGE_SIZE = 20;

// ─── Route ────────────────────────────────────────────────────────────────────

const searchSchema = z.object({
  user_type: z.string().default('artist'),
  city: z.string().optional(),
});

export const Route = createFileRoute('/user-filter')({
  validateSearch: (search: Record<string, unknown>) => searchSchema.parse(search),
  component: UserFilterPage,
});

// ─── Page Component ───────────────────────────────────────────────────────────

function UserFilterPage() {
  const { user_type, city } = Route.useSearch();
  const navigate = useNavigate();

  const [users, setUsers] = useState<FilteredUser[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brokenAvatarIds, setBrokenAvatarIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const loaderRef = useRef<HTMLDivElement>(null);

  // Combined Artist+DJ mode — user_type 'artist_dj' shows a tab switcher
  const isArtistDj = user_type === 'artist_dj';
  const [activeTab, setActiveTab] = useState<string>('artist');
  const effectiveUserType = isArtistDj ? activeTab : user_type;

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  const fetchUsers = useCallback(
    async (page: number, reset = false) => {
      if (reset) {
        setIsLoading(true);
        setError(null);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const params = new URLSearchParams({
          page: page.toString(),
          page_size: PAGE_SIZE.toString(),
          user_type: effectiveUserType,
        });
        if (city && city !== 'All Locations') {
          params.set('city', city);
        }
        if (debouncedQuery) {
          params.set('q', debouncedQuery);
        }

        const { data } = await apiClient.get<FilteredUsersResponse>(
          `/custom_account/user-filter/?${params.toString()}`,
        );

        const results: FilteredUser[] = data?.results ?? [];

        setUsers((prev) => (reset ? results : [...prev, ...results]));
        setTotalCount(data?.count ?? 0);
        setHasMore(results.length === PAGE_SIZE && data?.next != null);
        setCurrentPage(page);
      } catch {
        setError('Failed to load users. Please try again.');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [effectiveUserType, city, debouncedQuery],
  );

  // Scroll to top whenever the page is first mounted, filters change, or tab switches
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [effectiveUserType, city, debouncedQuery]);

  // Re-fetch when filters change or tab switches
  useEffect(() => {
    setUsers([]);
    setCurrentPage(1);
    setHasMore(true);
    setBrokenAvatarIds(new Set());
    fetchUsers(1, true);
  }, [effectiveUserType, city, debouncedQuery, fetchUsers]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const sentinel = loaderRef.current;
    if (!sentinel || !hasMore || isLoading || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchUsers(currentPage + 1);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore, currentPage, fetchUsers]);

  const title = isArtistDj
    ? city && city !== 'All Locations'
      ? `DJs & Artists in ${city}`
      : 'All DJs & Artists'
    : getPageTitle(user_type, city);
  const subtitle = isArtistDj
    ? city && city !== 'All Locations'
      ? `Browse DJs & Artists based in ${city}`
      : 'Browse all DJs & Artists on Musicade'
    : getPageSubtitle(user_type, city);

  const ARTIST_DJ_TABS = [
    { id: 'artist', label: 'Artists' },
    { id: 'dj', label: 'DJs' },
  ] as const;

  if (isLoading) {
    return <LoadingLogo />;
  }

  return (
    <div className="min-h-screen bg-black pb-32">
      <Header />

      <main className="px-4 sm:px-6 lg:px-8 pt-24 md:pt-20 pb-8">
        {/* ── Back + Title ──────────────────────────────────────────────── */}
        <div className="flex items-start gap-4 mb-6">
          <button
            onClick={() => navigate({ to: '/' })}
            className="mt-1 w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white hover:border-white/20 hover:bg-white/5 transition-all shrink-0"
          >
            <FiArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">{title}</h1>
            <p className="text-zinc-500 text-[14px] mt-1">{subtitle}</p>
            {!isLoading && totalCount > 0 && (
              <p className="text-zinc-600 text-[13px] mt-0.5">
                {totalCount.toLocaleString()} result{totalCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>

        {/* ── Artist / DJ tabs (combined mode only) ─────────────────────── */}
        {isArtistDj && (
          <div className="flex gap-1 p-1 bg-zinc-900 border border-white/5 rounded-xl mb-6 w-fit">
            {ARTIST_DJ_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-2 rounded-lg text-[13px] font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* ── Search ─────────────────────────────────────────────────────── */}
        <div className="relative mb-6">
          <FiSearch
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={`Search ${isArtistDj ? 'DJs & Artists' : `${USER_TYPE_LABELS[user_type] ?? user_type}s`}`}
            className="w-full rounded-xl border border-white/10 bg-zinc-950 text-white placeholder:text-zinc-500 pl-11 pr-4 py-3 text-[14px] outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/30 transition-colors"
          />
        </div>

        {/* ── Error state ───────────────────────────────────────────────── */}
        {error && (
          <div className="flex flex-col items-center justify-center py-20 text-center gap-4">
            <p className="text-red-400 text-sm">{error}</p>
            <button
              onClick={() => fetchUsers(1, true)}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-blue-600 text-white text-[14px] font-semibold hover:bg-blue-500 transition-colors"
            >
              <FiRefreshCw size={14} /> Retry
            </button>
          </div>
        )}

        {/* ── Empty state ───────────────────────────────────────────────── */}
        {!error && users.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
            <FiUsers size={48} className="text-zinc-700" />
            <p className="text-zinc-300 font-semibold text-lg">No users found</p>
            <p className="text-zinc-600 text-[14px]">
              {debouncedQuery
                ? `No results for "${debouncedQuery}". Try another name or username.`
                : 'Try selecting a different location'}
            </p>
            <button
              onClick={() => navigate({ to: '/' })}
              className="mt-2 px-5 py-2.5 rounded-xl bg-zinc-800 text-white text-[14px] font-semibold hover:bg-zinc-700 transition-colors border border-white/5"
            >
              Back to Home
            </button>
          </div>
        )}

        {/* ── User list ─────────────────────────────────────────────────── */}
        {users.length > 0 && (
          <div className="space-y-3">
            {users.map((user) => {
              const avatar = getDiscoverAvatarUrl(user);
              const isAvatarBroken = brokenAvatarIds.has(user.id);
              const displayName = user.full_name || user.username || `User ${user.id}`;
              const handle = getProfileIdentifier({ id: user.id, username: user.username });
              const primaryType = user.user_types?.[0]?.name ?? '';
              const location = [user.city, user.state].filter(Boolean).join(', ');
              const genres = (user.genres ?? []).slice(0, 3);

              return (
                <Link
                  key={user.id}
                  to="/profile/$identifier"
                  params={{ identifier: handle }}
                  className="group flex items-center gap-4 p-4 rounded-xl bg-zinc-950 border border-white/5 hover:border-blue-500/20 hover:bg-zinc-900/60 transition-all duration-200"
                >
                  {/* Avatar */}
                  <div className="shrink-0">
                    {avatar && !isAvatarBroken ? (
                      <img
                        src={avatar}
                        alt={displayName}
                        className="w-14 h-14 rounded-full object-cover border border-zinc-800 group-hover:border-blue-500/40 transition-colors"
                        onError={() =>
                          setBrokenAvatarIds((prev) => {
                            if (prev.has(user.id)) return prev;
                            const next = new Set(prev);
                            next.add(user.id);
                            return next;
                          })
                        }
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center group-hover:border-blue-500/40 transition-colors">
                        <span className="text-xl font-bold text-zinc-500 group-hover:text-blue-400 transition-colors">
                          {displayName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-white font-semibold text-[15px] truncate group-hover:text-blue-400 transition-colors">
                        {displayName}
                      </p>
                      <FiArrowRight
                        size={14}
                        className="text-zinc-600 group-hover:text-zinc-300 shrink-0 ml-2 transition-colors"
                      />
                    </div>

                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      {primaryType && (
                        <span className="flex items-center gap-1 text-[12px] text-blue-400 font-medium">
                          <FiUser size={11} />
                          {primaryType}
                        </span>
                      )}
                      {location && (
                        <span className="flex items-center gap-1 text-[12px] text-zinc-500">
                          <FiMapPin size={11} />
                          {location}
                        </span>
                      )}
                    </div>

                    {genres.length > 0 && (
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        {genres.map((g) => (
                          <span
                            key={g}
                            className="px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-400 text-[11px] font-medium border border-blue-500/10"
                          >
                            {g}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}

            {/* Infinite scroll sentinel */}
            <div ref={loaderRef} className="py-6 flex justify-center">
              {isLoadingMore && (
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              )}
            </div>

            {!hasMore && users.length > 0 && (
              <p className="text-center text-zinc-700 text-[13px] py-4">
                All {users.length} results loaded
              </p>
            )}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
